import { Link } from 'react-router-dom';
import InlineCTA from './InlineCTA';
import BlogImage from '../components/BlogImage';

export default function Mlb2026BettingGuide() {
  return (
    <article style={{ maxWidth: 820, margin: '0 auto', padding: '40px 20px', color: '#e0e0e0', fontFamily: 'sans-serif', lineHeight: 1.8 }}>

      <p style={{ color: '#999', fontSize: 14, marginBottom: 32 }}>
        最後更新：2026-03-18 ｜ 閱讀時間：約 18 分鐘
      </p>

      <BlogImage src="/assets/images/articles/mlb-2026-betting-guide-hero.webp" alt="MLB 2026 球季運彩投注指南：棒球場與數據分析面板" caption="MLB 2026 運彩投注指南" width={1200} height={675} priority={true} />

      <p>
        2026 MLB 球季即將在 3 月底開打，162 場漫長的例行賽將提供大量投注機會。
        棒球被稱為「數據驅動」的運動，先發投手、打線狀態、主客場紀錄等因素都直接影響比賽結果，
        這也讓 MLB 成為最適合運用數據分析進行投注的運動項目之一。
      </p>
      <p>
        本篇指南專為想在 2026 球季投注 MLB 運彩的玩家撰寫，從基礎盤口類型到進階投注策略，
        幫你建立完整的棒球運彩知識。如果你是運彩新手，建議先閱讀
        <Link to="/blog/sports-betting-guide" style={{ color: '#93c5fd' }}>運彩新手完全指南</Link>打好基礎。
      </p>

      <nav aria-label="文章目錄" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: 12, padding: '16px 20px', margin: '24px 0' }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}>本文目錄</p>
        <ol style={{ paddingLeft: 20, margin: 0, lineHeight: 2 }}>
          <li><a href="#season-overview" style={{ color: '#93c5fd' }}>2026 MLB 球季概覽</a></li>
          <li><a href="#key-events" style={{ color: '#93c5fd' }}>2026 球季重點賽事預覽</a></li>
          <li><a href="#basics" style={{ color: '#93c5fd' }}>棒球運彩基礎盤口</a></li>
          <li><a href="#odds-explained" style={{ color: '#93c5fd' }}>MLB 運彩賠率解讀實例</a></li>
          <li><a href="#strategy" style={{ color: '#93c5fd' }}>六大投注策略深度解析</a></li>
          <li><a href="#mistakes" style={{ color: '#93c5fd' }}>新手常犯的 5 個錯誤</a></li>
          <li><a href="#platform" style={{ color: '#93c5fd' }}>合法投注管道</a></li>
          <li><a href="#bankroll" style={{ color: '#93c5fd' }}>資金管理建議</a></li>
          <li><a href="#monthly-plan" style={{ color: '#93c5fd' }}>月度投注計畫表</a></li>
          <li><a href="#faq" style={{ color: '#93c5fd' }}>FAQ 常見問題</a></li>
        </ol>
      </nav>

      {/* ===== 第一章：球季概覽 ===== */}
      <h2 id="season-overview" style={{ color: '#d4af37', marginTop: 48 }}>一、2026 MLB 球季概覽</h2>
      <p>
        2026 年 MLB 延續 162 場例行賽制，球季預計從 3 月底開打至 9 月底結束，
        隨後進入季後賽。以下是本季值得關注的重點：
      </p>
      <ul>
        <li><strong>規則調整：</strong>2026 年持續沿用投球計時器（Pitch Clock）和限制守備佈陣規則，比賽節奏預期維持較快，場均時間約 2 小時 35 分鐘左右</li>
        <li><strong>熱門奪冠隊伍：</strong>道奇、洋基、勇士等傳統強隊仍是季前熱門；太空人重建中但不可忽視，費城人、巴爾的摩金鶯也被看好</li>
        <li><strong>重要自由球員動態：</strong>休賽季的交易與簽約影響各隊戰力消長，投注前應確認最新陣容</li>
        <li><strong>球季長度優勢：</strong>162 場例行賽提供極大的樣本量，長期數據分析在 MLB 中特別有效</li>
        <li><strong>擴大季後賽制：</strong>12 隊季後賽制延續，外卡三輪淘汰戰提供更多高張力投注機會</li>
      </ul>

      <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 10, padding: '16px 20px', margin: '24px 0' }}>
        <p style={{ color: '#eab308', fontWeight: 700, marginBottom: 6 }}>投注小提醒</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          MLB 強隊的長期勝率通常只在 58-62%，弱隊爆冷的頻率比 NBA 或足球更高。
          這意味著單場投注的風險較高，但也代表盤口經常出現有價值的機會。
          想深入了解如何評估價值，可參考<Link to="/blog/sports-betting-analysis" style={{ color: '#93c5fd' }}>運彩分析完全指南</Link>。
        </p>
      </div>

      {/* ===== 第二章：重點賽事預覽 ===== */}
      <h2 id="key-events" style={{ color: '#d4af37', marginTop: 48 }}>二、2026 球季重點賽事預覽</h2>
      <p>
        掌握球季關鍵時間節點，有助於規劃投注策略與資金分配。以下是 2026 年 MLB 球季的重要賽事時程：
      </p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>賽事</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>預計時間</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>投注看點</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['春訓熱身賽', '2-3 月', '觀察新陣容磨合、新秀表現，為開季投注做準備'],
              ['開幕戰', '3 月底', '首戰獨贏盤受關注，注意各隊開幕先發投手安排'],
              ['跨聯盟賽', '6 月', '美聯 vs 國聯跨聯盟交手，對戰數據較少，冷門頻出'],
              ['MLB 全明星賽', '7 月中旬', '全明星週提供全壘打大賽等特殊投注盤口'],
              ['交易大限', '7 月底', '球隊補強/拋售球員，戰力重新洗牌，賠率劇烈波動'],
              ['例行賽最後月', '9 月', '季後賽席位白熱化爭奪，球隊動機成為關鍵變數'],
              ['外卡系列賽', '10 月初', '三戰兩勝制，短系列賽波動大，先發投手決定一切'],
              ['分區系列賽 (ALDS/NLDS)', '10 月中', '五戰三勝制，牛棚深度和板凳戰力凸顯'],
              ['冠軍系列賽 (ALCS/NLCS)', '10 月中下旬', '七戰四勝制，教練團戰術博弈加重'],
              ['世界大賽', '10 月底-11 月初', '全年最高關注度，獨贏盤與系列賽總場數盤最熱門'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', color: '#e0e0e0' }}>{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>季後賽投注特別注意事項</h3>
      <p>
        季後賽和例行賽的投注邏輯截然不同。例行賽重視長期數據和趨勢，季後賽則著重以下幾點：
      </p>
      <ul>
        <li><strong>先發投手輪值：</strong>季後賽投手可以獲得更多休息日，王牌投手可能每隔 3-4 天就上場一次，而非例行賽的 5 天輪值</li>
        <li><strong>經驗值：</strong>曾多次征戰季後賽的老將和教練團在關鍵時刻表現更穩定</li>
        <li><strong>主場優勢放大：</strong>季後賽主場勝率約 55-57%，高於例行賽的 54%，球場氣氛影響更大</li>
        <li><strong>短系列賽波動：</strong>外卡賽三戰兩勝制的不確定性極高，黑馬頻出</li>
      </ul>

      <InlineCTA />

      {/* ===== 第三章：基礎盤口 ===== */}
      <h2 id="basics" style={{ color: '#d4af37', marginTop: 48 }}>三、棒球運彩基礎盤口</h2>
      <p>
        MLB 運彩的盤口類型和其他運動略有不同，主要有三種基本玩法。
        更多盤口基礎知識可參考<Link to="/blog/sports-betting-analysis" style={{ color: '#93c5fd' }}>運彩分析完全指南</Link>。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>3.1 獨贏盤（Moneyline）</h3>
      <p>
        MLB 最常見的投注方式，純粹預測哪一隊會贏。由於棒球比賽分差通常不大，
        獨贏盤比讓分盤更受歡迎。賠率會根據兩隊先發投手的實力差距產生明顯變化——
        王牌投手先發時，球隊的獨贏賠率可能從 2.10 降到 1.60。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>3.2 讓分盤（Run Line）</h3>
      <p>
        MLB 的讓分盤通常固定為 <strong>1.5 分</strong>，也就是強隊讓 1.5 分（-1.5）、弱隊受讓 1.5 分（+1.5）。
        讓分盤的賠率波動比獨贏盤大：強隊讓 1.5 分的賠率通常在 2.00 以上，
        因為棒球比賽有大量 1 分差的結果（約佔所有比賽的 30%）。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>3.3 大小分（Over/Under）</h3>
      <p>
        預測兩隊的總得分會超過（Over）或低於（Under）莊家設定的數字。
        MLB 的大小分通常設在 <strong>7.5-9.5 分</strong>之間，具體數字取決於兩隊先發投手的壓制力和球場因素。
        例如在打者天堂 Coors Field（高海拔球場），大小分會被調高到 10 分以上。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>3.4 其他熱門盤口</h3>
      <p>除了三大基本盤口，MLB 還有以下進階玩法：</p>
      <ul>
        <li><strong>前五局獨贏（First 5 Innings）：</strong>只看前五局結果，排除牛棚不確定性，適合專注分析先發投手對決</li>
        <li><strong>球員個人盤：</strong>預測某打者安打數、投手三振數等個人數據</li>
        <li><strong>系列賽勝負盤：</strong>預測一組三連戰或四連戰的系列賽贏家</li>
        <li><strong>季後賽特別盤：</strong>世界大賽冠軍、聯盟冠軍、MVP 等長期盤口</li>
      </ul>

      <div className="my-8 p-4 bg-[#111] rounded-lg border border-yellow-500/10">
        <h3 className="text-lg font-bold text-yellow-400 mb-3">MLB 主要盤口速查表</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 text-gray-400">盤口類型</th>
                <th className="text-left py-2 text-gray-400">說明</th>
                <th className="text-left py-2 text-gray-400">適合情境</th>
                <th className="text-left py-2 text-gray-400">新手建議</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-gray-800"><td className="py-2 font-bold">獨贏盤</td><td className="py-2">猜哪隊贏</td><td className="py-2">有明確勝負判斷時</td><td className="py-2 text-green-400">最推薦</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 font-bold">讓分盤 1.5</td><td className="py-2">強隊需贏 2 分以上</td><td className="py-2">認為強隊會大勝</td><td className="py-2">中階</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 font-bold">大小分</td><td className="py-2">猜兩隊總得分高低</td><td className="py-2">對投手對決有分析</td><td className="py-2">中階</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 font-bold">前五局獨贏</td><td className="py-2">只看前五局結果</td><td className="py-2">先發投手分析有信心</td><td className="py-2 text-green-400">推薦</td></tr>
              <tr><td className="py-2 font-bold">球員個人盤</td><td className="py-2">猜個人數據表現</td><td className="py-2">對特定球員有研究</td><td className="py-2">進階</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== 第四章：賠率解讀 ===== */}
      <h2 id="odds-explained" style={{ color: '#d4af37', marginTop: 48 }}>四、MLB 運彩賠率解讀實例</h2>
      <p>
        MLB 運彩使用「美式賠率」（American Odds）是國際主流，但台灣運彩和多數線上平台也提供小數賠率。
        理解賠率換算是投注的基本功。建議搭配<Link to="/tools/odds-converter" style={{ color: '#93c5fd' }}>賠率換算器</Link>進行練習。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>4.1 美式賠率基礎</h3>
      <p>美式賠率分為正值（+）和負值（-）兩種：</p>
      <ul>
        <li><strong>負值（-）= 熱門方：</strong>數字代表「要下注多少才能贏 100 元」。例如 -150 表示下注 150 元可贏 100 元</li>
        <li><strong>正值（+）= 冷門方：</strong>數字代表「下注 100 元可以贏多少」。例如 +130 表示下注 100 元可贏 130 元</li>
      </ul>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>4.2 實戰範例：道奇 vs 響尾蛇</h3>
      <p>假設一場道奇客場對響尾蛇的比賽，盤口如下：</p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>盤口</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>道奇</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>響尾蛇</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['獨贏盤（美式）', '-160', '+140'],
              ['獨贏盤（小數）', '1.63', '2.40'],
              ['讓分盤 -1.5/+1.5', '+110', '-130'],
              ['大小分 8.5', 'Over -105', 'Under -115'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>4.3 報酬計算實例</h3>
      <p>以上述盤口為例，假設你的投注金額為 NT$1,000：</p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>投注選項</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>投注金額</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>若勝利的淨利</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>總回收</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>隱含勝率</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['道奇獨贏 (-160)', 'NT$1,000', 'NT$625', 'NT$1,625', '61.5%'],
              ['響尾蛇獨贏 (+140)', 'NT$1,000', 'NT$1,400', 'NT$2,400', '41.7%'],
              ['道奇讓 1.5 (+110)', 'NT$1,000', 'NT$1,100', 'NT$2,100', '47.6%'],
              ['大分 Over 8.5 (-105)', 'NT$1,000', 'NT$952', 'NT$1,952', '51.2%'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#4ade80' }}>{row[2]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[3]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[4]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 10, padding: '16px 20px', margin: '24px 0' }}>
        <p style={{ color: '#eab308', fontWeight: 700, marginBottom: 6 }}>賠率價值判斷公式</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          <strong>期望值 = （你預估的勝率 × 淨利）-（你預估的敗率 × 投注金額）</strong>
          <br />
          例如：你認為道奇贏的機率是 65%（高於隱含勝率 61.5%），則期望值 = 0.65 × 625 - 0.35 × 1000 = +56.25。
          期望值為正數代表這是一筆有價值的投注。用<Link to="/tools/odds-converter" style={{ color: '#93c5fd' }}>賠率換算器</Link>可快速完成計算。
        </p>
      </div>

      <InlineCTA />

      {/* ===== 第五章：六大策略 ===== */}
      <h2 id="strategy" style={{ color: '#d4af37', marginTop: 48 }}>五、六大投注策略深度解析</h2>
      <p>
        以下六大策略是 MLB 投注的核心分析框架。每一項都有具體的數據指標和判斷方法，
        搭配<Link to="/blog/sports-betting-analysis" style={{ color: '#93c5fd' }}>運彩分析完全指南</Link>中的系統化方法，可以大幅提升你的投注勝率。
      </p>

      {/* 策略一 */}
      <h3 style={{ color: '#d4af37', marginTop: 32 }}>策略一：先發投手 ERA/WHIP 分析法</h3>
      <p>
        一場 MLB 比賽的結果，超過 <strong>40%</strong> 取決於先發投手的對決。
        以下是投注前必須檢查的核心投手數據：
      </p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>數據指標</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>定義</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>頂級水準</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>聯盟平均</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>低於平均</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['ERA（自責分率）', '每 9 局平均自責失分', '< 3.00', '3.80-4.20', '> 4.80'],
              ['WHIP', '每局被上壘率', '< 1.10', '1.25-1.35', '> 1.45'],
              ['K/9（每 9 局三振數）', '壓制打者的能力', '> 10.0', '8.0-9.0', '< 6.5'],
              ['BB/9（每 9 局保送數）', '控球穩定度', '< 2.0', '2.8-3.5', '> 4.0'],
              ['FIP', '投手獨立防禦率', '< 3.20', '3.80-4.30', '> 4.80'],
              ['xFIP', '預期投手獨立防禦率', '< 3.40', '3.90-4.30', '> 4.70'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#4ade80' }}>{row[2]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[3]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#ef4444' }}>{row[4]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 style={{ color: '#e0e0e0', marginTop: 24 }}>實例計算：先發投手對決評分</h4>
      <p>假設今天的比賽是洋基對紅襪，兩隊先發投手數據如下：</p>
      <ul>
        <li><strong>洋基先發：</strong>ERA 2.85 / WHIP 1.05 / K/9 11.2 / 近 5 場 ERA 2.40</li>
        <li><strong>紅襪先發：</strong>ERA 4.35 / WHIP 1.38 / K/9 7.8 / 近 5 場 ERA 5.10</li>
      </ul>
      <p>
        <strong>分析結論：</strong>洋基先發在所有關鍵指標上全面領先，且近況持續升溫。
        若盤口洋基獨贏賠率在 1.65（隱含勝率 60.6%）以上，考量先發投手的巨大差距，
        洋基實際勝率可能接近 63-66%，這是一筆有正期望值的投注。
      </p>

      <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 10, padding: '16px 20px', margin: '24px 0' }}>
        <p style={{ color: '#eab308', fontWeight: 700, marginBottom: 6 }}>進階技巧：關注 ERA vs FIP 差距</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          如果投手的 ERA 遠低於 FIP（例如 ERA 2.80 但 FIP 3.90），代表他可能有運氣成分，
          未來 ERA 有回升風險。反之，ERA 遠高於 FIP 的投手可能被低估，是尋找價值的好機會。
        </p>
      </div>

      {/* 策略二 */}
      <h3 style={{ color: '#d4af37', marginTop: 32 }}>策略二：主場優勢數據分析</h3>
      <p>
        MLB 的整體主場勝率約 <strong>53-54%</strong>，但不同球場和球隊的主場表現差異極大。
        以下是影響主場優勢的關鍵因素：
      </p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>球場/因素</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>特性</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>投注影響</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Coors Field（洛磯）', '海拔 1,580 公尺', '大小分大幅偏高（場均多 2-3 分），變化球效果減弱'],
              ['Yankee Stadium（洋基）', '右外野短牆 96 公尺', '左打全壘打偏多，注意打線左右手配置'],
              ['Oracle Park（巨人）', '海風強勁的投手球場', '壓低得分，偏向小分，右外野受海風影響大'],
              ['Wrigley Field（小熊）', '風向每日變化極大', '吹出去時大分場，吹進來時小分場，賽前必查風向'],
              ['Tropicana Field（光芒）', '室內球場（巨蛋）', '不受天氣影響，數據一致性高，適合數據分析'],
              ['Fenway Park（紅襪）', '綠色怪物左外野高牆', '二壘打偏多，打擊數據需特別校正'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', color: '#e0e0e0' }}>{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        <strong>主場優勢應用技巧：</strong>比較球隊的主客場勝率差距。如果某支球隊主場勝率 60% 但客場只有 45%，
        那麼當他們在主場面對中等實力對手時，市場可能低估了他們的主場優勢。
        同理，分析客場戰績極差的球隊在作客時可能被高估。想查看更多主場優勢的歷史數據與趨勢，可以參考主場優勢數據分析。
      </p>

      {/* 策略三 */}
      <h3 style={{ color: '#d4af37', marginTop: 32 }}>策略三：連勝/連敗動能判斷</h3>
      <p>
        棒球是一項「條紋運動」——球隊經常出現連勝或連敗的趨勢。以下是歷年統計數據的經驗法則：
      </p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>連勝/連敗場次</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>下一場勝率（連勝方）</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>投注策略建議</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['連勝 3 場', '約 56%', '動能仍在，可順勢投注但注意賠率是否已反映'],
              ['連勝 4-5 場', '約 54-55%', '動能稍減，賠率通常已壓低，價值下降'],
              ['連勝 6 場以上', '約 51-52%', '回歸均值效應明顯，避免追高'],
              ['連敗 3 場', '對手勝率約 55%', '球隊可能調整心態，注意反彈機會'],
              ['連敗 5 場以上', '對手勝率約 53%', '弱隊可能觸底反彈，強隊連敗則需檢視傷兵'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', color: '#e0e0e0' }}>{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        <strong>關鍵提醒：</strong>連勝/連敗只是輔助指標，不應作為唯一判斷依據。
        連勝時如果下一場面對對方王牌投手、或是長途客場旅行的第一場，動能可能被打斷。
        永遠要將趨勢與其他因素（投手、主客場、休息天數）綜合考量。
      </p>

      {/* 策略四 */}
      <h3 style={{ color: '#d4af37', marginTop: 32 }}>策略四：牛棚疲勞度追蹤</h3>
      <p>
        先發投手通常只負責前 5-6 局，後面 3-4 局由牛棚接手。
        牛棚疲勞是 MLB 投注中最被散戶忽略、但影響巨大的因素之一。
      </p>

      <h4 style={{ color: '#e0e0e0', marginTop: 24 }}>牛棚疲勞度評估方法</h4>
      <p>追蹤每支球隊後援投手群在<strong>近 3 天的用球數</strong>，判斷疲勞程度：</p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>近 3 天用球數</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>疲勞等級</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>投注影響</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['單一後援投手投 60+ 球', '高度疲勞', '該投手大概率不會上場，牛棚深度打折'],
              ['三天內牛棚合計 100+ 球', '中度疲勞', '教練可能讓先發多投，或用較弱的後援投手'],
              ['終結者連續兩天出賽', '高度疲勞', '九局可能改用 B 計畫，影響護航能力'],
              ['牛棚全員低用球數', '充沛', '後段安全網完整，有利於球隊保持領先'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: i === 3 ? '#4ade80' : '#ef4444' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', color: '#e0e0e0' }}>{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        <strong>實戰應用：</strong>如果 A 隊的牛棚近三天已消耗大量投球數，即使先發投手數據佔優，
        後段比賽的風險也會大幅增加。這種情況下，「前五局獨贏」可能比全場獨贏更安全。
        相關資金控制方法請參考<Link to="/blog/bankroll-management-guide" style={{ color: '#93c5fd' }}>資金管理完全指南</Link>。
      </p>

      {/* 策略五 */}
      <h3 style={{ color: '#d4af37', marginTop: 32 }}>策略五：天氣與場地因素</h3>
      <p>
        棒球是所有主流運動中最受天氣影響的項目之一。以下因素會直接影響比賽得分和投注判斷：
      </p>

      <h4 style={{ color: '#e0e0e0', marginTop: 24 }}>風速對大小分的影響</h4>
      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>風向/風速</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>對得分的影響</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>大小分建議</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['吹往外野 > 15 km/h', '得分顯著增加', '偏向大分（Over），全壘打機率上升'],
              ['吹向本壘 > 15 km/h', '得分明顯減少', '偏向小分（Under），飛球被壓回場內'],
              ['橫向風 > 20 km/h', '投手控球受影響', '保送增加但全壘打不一定增加，看具體情況'],
              ['無風或微風', '正常狀態', '回歸基本數據分析'],
              ['氣溫 > 30°C', '球的彈性增加', '偏向大分，球飛得更遠'],
              ['氣溫 < 10°C', '球的彈性降低', '偏向小分，打擊距離縮短'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', color: '#e0e0e0' }}>{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        <strong>其他場地因素：</strong>露天球場（如 Wrigley Field、Dodger Stadium）受天氣影響最大，
        室內球場（如 Tropicana Field）和可關合屋頂球場（如 Chase Field）則完全排除天氣變數。
        下雨延賽也會打亂投手輪值，間接影響後續幾場比賽的投注判斷。
      </p>

      {/* 策略六 */}
      <h3 style={{ color: '#d4af37', marginTop: 32 }}>策略六：對戰歷史記錄分析</h3>
      <p>
        棒球的對戰歷史（Head-to-Head）數據量龐大，是其他運動難以比擬的。以下是分析對戰記錄時的重點：
      </p>
      <ul>
        <li><strong>投手 vs 打線歷史：</strong>某些投手對特定打線有天生剋星效應。例如投手 A 面對紅襪可能生涯 ERA 只有 2.50，但面對洋基卻高達 5.20</li>
        <li><strong>個別打者 vs 投手：</strong>查看對方主力打者對今日先發投手的歷史打擊數據（打擊率、全壘打數、三振率）</li>
        <li><strong>場地對戰紀錄：</strong>部分球隊在特定客場表現特別好或特別差，例如某些球隊在 Coors Field 的打擊數據會暴漲</li>
        <li><strong>近期對戰：</strong>同一組三連戰中，第一場輸的球隊在第二場會有調整，反彈機率約 52-54%</li>
      </ul>

      <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 10, padding: '16px 20px', margin: '24px 0' }}>
        <p style={{ color: '#eab308', fontWeight: 700, marginBottom: 6 }}>對戰數據使用注意事項</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          對戰歷史需要足夠的樣本量才有參考價值。如果投手 A 只面對過紅襪 3 次，數據波動太大不具代表性。
          建議至少要有 <strong>5 場以上</strong>的對戰數據才納入分析。另外，超過 2 年以上的數據要打折，
          因為球員能力會隨時間變化。串關計算可用<Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>串關計算器</Link>。
        </p>
      </div>

      <InlineCTA />

      {/* ===== 第六章：新手常犯錯誤 ===== */}
      <h2 id="mistakes" style={{ color: '#d4af37', marginTop: 48 }}>六、新手常犯的 5 個錯誤</h2>
      <p>
        以下是 MLB 運彩新手最容易犯的五個錯誤。
        避開這些坑，你的長期勝率就已經贏過大部分散戶了。
      </p>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ color: '#ef4444', fontSize: 18, marginBottom: 12 }}>錯誤一：追星下注（只看隊名下注）</h3>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>症狀：</strong>永遠只投注道奇、洋基等明星球隊，不管對手是誰、先發投手是誰。
        </p>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>問題：</strong>莊家深知散戶偏好熱門球隊，會刻意壓低熱門隊的賠率。長期投注 -180 以上的大熱門，
          你的勝率需要超過 64% 才能損益兩平——這在 MLB 幾乎不可能。
        </p>
        <p style={{ fontSize: 14, margin: 0 }}>
          <strong style={{ color: '#4ade80' }}>修正方法：</strong>不看隊名，先看數據。用先發投手 ERA、牛棚狀態、主客場紀錄等客觀指標做判斷。
          冷門隊配上王牌投手，經常是被低估的高價值投注。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ color: '#ef4444', fontSize: 18, marginBottom: 12 }}>錯誤二：忽略牛棚狀態</h3>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>症狀：</strong>只看先發投手就下注，完全不查牛棚近期投球量。
        </p>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>問題：</strong>MLB 比賽約 40% 的得分發生在第 7 局之後。如果球隊牛棚連續三天高負荷出賽，
          即使先發投手表現完美，後段被逆轉的風險也極高。
        </p>
        <p style={{ fontSize: 14, margin: 0 }}>
          <strong style={{ color: '#4ade80' }}>修正方法：</strong>養成賽前查看牛棚近 3 天用球數的習慣。
          當牛棚疲勞時，考慮改投「前五局獨贏」，或完全跳過這場比賽。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ color: '#ef4444', fontSize: 18, marginBottom: 12 }}>錯誤三：盲目串關追求高賠率</h3>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>症狀：</strong>每天開 4-5 場串關，貪圖 10 倍以上的高賠率。
        </p>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>問題：</strong>MLB 單場投注的勝率上限約 58-60%。串 4 場的理論勝率 = 0.58^4 = 11.3%，
          但串關賠率通常只有 8-10 倍，長期期望值為負。串關場次越多，莊家抽水效應被放大得越嚴重。
        </p>
        <p style={{ fontSize: 14, margin: 0 }}>
          <strong style={{ color: '#4ade80' }}>修正方法：</strong>串關最多 2-3 場，且每場都要有充分的數據支撐。
          可用<Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>串關計算器</Link>事先計算期望值，
          確認串關組合是否合理。更好的做法是以單場投注為主。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ color: '#ef4444', fontSize: 18, marginBottom: 12 }}>錯誤四：不做資金管理</h3>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>症狀：</strong>有感覺就重壓，連輸時加碼想翻本，沒有固定的單注金額。
        </p>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>問題：</strong>MLB 球季長達 6 個月，任何人都會遭遇連敗期。如果沒有資金管理，
          一次連輸 8-10 場就可能燒光整季預算。
        </p>
        <p style={{ fontSize: 14, margin: 0 }}>
          <strong style={{ color: '#4ade80' }}>修正方法：</strong>每注控制在總資金的 1-3%，設定每日止損線（5%）。
          連輸時減碼而非加碼。詳細方法請參考<Link to="/blog/bankroll-management-guide" style={{ color: '#93c5fd' }}>資金管理完全指南</Link>。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ color: '#ef4444', fontSize: 18, marginBottom: 12 }}>錯誤五：忽略球場和天氣因素</h3>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>症狀：</strong>只看球隊和投手數據，完全不考慮比賽場地和當天天氣。
        </p>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          <strong>問題：</strong>Coors Field 的場均得分比聯盟平均高出 30% 以上，Wrigley Field 的風向可以讓同一天的比賽在大分場和小分場之間搖擺。
          忽略這些因素等於放棄了一個巨大的資訊優勢。
        </p>
        <p style={{ fontSize: 14, margin: 0 }}>
          <strong style={{ color: '#4ade80' }}>修正方法：</strong>養成賽前查天氣預報的習慣（尤其是風向和氣溫）。
          對於露天球場的比賽，天氣因素應列入投注決策的核心考量。
        </p>
      </div>

      {/* ===== 第七章：合法投注管道 ===== */}
      <h2 id="platform" style={{ color: '#d4af37', marginTop: 48 }}>七、合法投注管道</h2>
      <p>
        在台灣，合法投注 MLB 只有台灣運彩一條管道：
      </p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>平台類型</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>抽水率</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>優點</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>缺點</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['台灣運彩（官方）', '10-15%', '政府特許、合法安全', '賠率較低、盤口選項較少'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', color: '#4ade80' }}>{row[2]}</td>
                <td style={{ padding: '10px 14px', color: '#ef4444' }}>{row[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        境外網站在台灣經營屬違法，玩家下注也有刑法第 266 條風險，出金糾紛無從求償，不建議使用。
        盤口判讀可參考<Link to="/blog/sports-betting-analysis" style={{ color: '#93c5fd' }}>運彩分析完全指南</Link>，
        並搭配<Link to="/tools/odds-converter" style={{ color: '#93c5fd' }}>賠率換算器</Link>換算隱含勝率。
      </p>

      {/* ===== 第八章：資金管理 ===== */}
      <h2 id="bankroll" style={{ color: '#d4af37', marginTop: 48 }}>八、資金管理建議</h2>
      <p>
        MLB 球季長達 6 個月，資金管理的重要性不言而喻。
        更完整的資金管理策略請參考<Link to="/blog/bankroll-management-guide" style={{ color: '#93c5fd' }}>資金管理完全指南</Link>。
      </p>
      <ul>
        <li><strong>單注控制在總資金的 1-3%：</strong>MLB 勝率波動大，較保守的注碼能讓你安全度過低潮期</li>
        <li><strong>設定每日/每週止損線：</strong>建議每日虧損達總資金 5% 時暫停投注，每週上限 15%</li>
        <li><strong>不追損：</strong>連輸時加碼是最快燒光資金的方式。每筆投注都應獨立決策</li>
        <li><strong>分級注碼：</strong>低信心（1 單位）、中信心（2 單位）、高信心（3 單位），嚴格不超過 3 單位</li>
        <li><strong>記錄每筆投注：</strong>詳細記錄日期、對戰、先發投手、盤口、賠率和結果，定期回顧找出自己的強項</li>
        <li><strong>定期回顧：</strong>每月底統計勝率、ROI（投資報酬率），檢視哪種盤口或策略表現最好</li>
      </ul>

      <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 10, padding: '16px 20px', margin: '24px 0' }}>
        <p style={{ color: '#eab308', fontWeight: 700, marginBottom: 6 }}>MLB 投注資金分配建議</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          假設你的月運彩預算是 NT$10,000，建議分成 20-30 個單位（每單位 $333-$500）。
          每場 MLB 投注不超過 1-2 個單位，遇到高信心場次最多加到 3 個單位。
          預留至少 30% 的資金應對連敗期。追蹤串關報酬可用<Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>串關計算器</Link>。
        </p>
      </div>

      {/* ===== 第九章：月度投注計畫表 ===== */}
      <h2 id="monthly-plan" style={{ color: '#d4af37', marginTop: 48 }}>九、月度投注計畫表</h2>
      <p>
        MLB 球季橫跨 7 個月，不同階段的投注策略和資金分配應有所不同。
        以下計畫表以月度為單位，幫助你在球季各階段做出最佳決策。
      </p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>月份</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>球季階段</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>建議投注量</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', color: '#fff' }}>資金分配</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#fff' }}>策略重點</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['3-4 月', '開季', '少量（每週 3-5 場）', '月預算的 15%', '觀察各隊真實戰力，不急著大量下注。春訓數據參考性低，等 2-3 週累積新數據'],
              ['5 月', '早季穩定期', '中量（每週 5-8 場）', '月預算的 15%', '各隊數據樣本量足夠，開始運用投手 ERA/WHIP 分析。關注傷兵回歸影響'],
              ['6 月', '跨聯盟賽', '中量（每週 5-8 場）', '月預算的 15%', '跨聯盟賽對戰數據少，冷門機會多。善用場地和天氣因素分析'],
              ['7 月', '明星賽+交易大限', '中量轉高量', '月預算的 18%', '交易大限前後戰力重新洗牌，注意球員移籍後的化學效應'],
              ['8 月', '季後賽爭奪白熱化', '高量（每週 8-12 場）', '月預算的 18%', '球隊動機明確（爭季後賽 vs 放棄擺爛），動機差異是最大投注優勢'],
              ['9 月', '例行賽衝刺', '高量（每週 8-12 場）', '月預算的 12%', '注意球隊是否「休兵」保留主力給季後賽，已確定淘汰的球隊拉上小聯盟新秀'],
              ['10-11 月', '季後賽', '精選（每輪 2-4 場）', '月預算的 7%', '短系列賽波動大，注碼保守。專注先發投手對決和季後賽經驗值分析'],
            ].map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1a2e' : '#16213e' }}>
                <td style={{ padding: '10px 14px', color: '#d4af37', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{row[0]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0', whiteSpace: 'nowrap' }}>{row[1]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0' }}>{row[2]}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center', color: '#e0e0e0', whiteSpace: 'nowrap' }}>{row[3]}</td>
                <td style={{ padding: '10px 14px', color: '#e0e0e0' }}>{row[4]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 10, padding: '16px 20px', margin: '24px 0' }}>
        <p style={{ color: '#eab308', fontWeight: 700, marginBottom: 6 }}>月度計畫執行要點</p>
        <ul style={{ margin: 0, fontSize: 14, paddingLeft: 20 }}>
          <li>每月初設定本月預算，用完即停，絕不透支下個月的額度</li>
          <li>每月底花 30 分鐘回顧投注紀錄，計算 ROI 和各策略勝率</li>
          <li>如果連續兩個月 ROI 為負，暫停一週重新檢視策略</li>
          <li>球季後半段（8-9 月）是最佳投注期，因為數據最充分、球隊動機最明確</li>
        </ul>
      </div>

      <InlineCTA />

      {/* ===== FAQ ===== */}
      <h2 id="faq" style={{ color: '#d4af37', marginTop: 48 }}>十、FAQ 常見問題</h2>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 8 }}>Q1：MLB 運彩新手應該從哪種盤口開始？</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          建議從獨贏盤（Moneyline）開始。獨贏盤只需判斷哪隊會贏，不需要考慮分差，是最直覺的投注方式。
          等熟悉之後再嘗試大小分和讓分盤。另一個適合新手的選項是「前五局獨贏」，
          因為它排除了牛棚的不確定性，讓你可以專注在先發投手分析上。
          基礎知識可參考<Link to="/blog/sports-betting-guide" style={{ color: '#93c5fd' }}>運彩新手完全指南</Link>。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 8 }}>Q2：先發投手臨時更換怎麼辦？</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          台灣運彩在先發投手更換時通常會調整賠率。線上平台部分會在投手更換後取消原有投注。
          投注前建議確認平台的投手更換政策，並在比賽當天再次確認先發投手名單。
          一般而言，MLB 球隊會在賽前 1-2 小時公布正式先發名單，建議在名單確認後再下注。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 8 }}>Q3：MLB 大小分怎麼判斷？</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          主要看五個因素：先發投手 ERA（低 ERA 傾向小分）、球場因素（高海拔球場傾向大分）、
          兩隊近期打擊狀態、當天天氣（風向和氣溫）、以及牛棚疲勞度。
          當雙方都派出 ERA 低於 3.00 的王牌投手，且球場偏向投手有利時，買小分通常是合理的選擇。
          反之，在 Coors Field 吹風的炎熱午場，大分幾乎是標配。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 8 }}>Q4：MLB 投注一天應該投幾場？</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          MLB 每天有 10-15 場比賽，但不代表每場都值得投注。建議新手每天精選 1-3 場有充分分析依據的比賽，
          寧可少投也不要為了行動而亂投。頂尖運彩玩家的經驗法則是：如果你找不到明確的價值（expected value 為正），
          那今天不投注就是最好的決策。品質永遠優於數量。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 8 }}>Q5：串關和單場投注哪個比較好？</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          就長期期望值而言，單場投注優於串關。串關的莊家抽水會隨場次增加而放大——
          串 2 場的總抽水約 8-10%，串 4 場則可能高達 20%。除非你對每一場都有很高的勝率信心（&gt;58%），
          否則建議以單場為主。如果想玩串關，最多串 2-3 場，並用<Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>串關計算器</Link>事先評估報酬率。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 8 }}>Q6：場中投注（Live Betting）適合 MLB 嗎？</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          MLB 的場中投注機會很多，因為 9 局的比賽有大量的轉折點。適合場中投注的情境包括：
          先發投手被提前換下、大幅落後的球隊在中段追分、以及牛棚更換時的賠率波動。
          但場中投注的決策時間短，不建議新手一開始就嘗試。先把賽前分析做紮實，
          等累積 2-3 個月的經驗後再接觸場中投注。
        </p>
      </div>

      <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 8 }}>Q7：哪裡可以查到 MLB 即時數據和投手資訊？</p>
        <p style={{ margin: 0, fontSize: 14 }}>
          以下是常用的免費 MLB 數據資源：MLB 官方網站提供即時比分和基礎數據；
          FanGraphs 提供進階數據分析（FIP、xFIP、WAR 等）；Baseball Reference 有完整的歷史數據；
          Weather.com 或 Windy 可查看球場天氣預報。建議每天花 15-20 分鐘瀏覽這些網站，
          掌握當日先發投手、傷兵消息和天氣狀況。
        </p>
      </div>

      {/* ===== 延伸閱讀 ===== */}
      <div style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: 12, padding: '20px', margin: '48px 0 0 0' }}>
        <p style={{ fontWeight: 700, marginBottom: 16, fontSize: 18, color: '#D4AF37' }}>延伸閱讀</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <li style={{ marginBottom: 12 }}>
            <Link to="/blog/sports-betting-guide" style={{ color: '#93c5fd' }}>運彩新手完全指南</Link>
            <span style={{ color: '#666', fontSize: 13 }}> — 從零開始的運彩入門教學</span>
          </li>
          <li style={{ marginBottom: 12 }}>
            <Link to="/blog/sports-betting-analysis" style={{ color: '#93c5fd' }}>運彩分析完全指南</Link>
            <span style={{ color: '#666', fontSize: 13 }}> — 進階數據分析與盤口解讀</span>
          </li>
          <li style={{ marginBottom: 12 }}>
            <Link to="/blog/bankroll-management-guide" style={{ color: '#93c5fd' }}>資金管理完全指南</Link>
            <span style={{ color: '#666', fontSize: 13 }}> — 科學化資金分配策略</span>
          </li>
          <li style={{ marginBottom: 12 }}>
            <Link to="/tools/odds-converter" style={{ color: '#93c5fd' }}>賠率換算器</Link>
            <span style={{ color: '#666', fontSize: 13 }}> — 快速比較不同格式賠率</span>
          </li>
          <li style={{ marginBottom: 0 }}>
            <Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>串關計算器</Link>
            <span style={{ color: '#666', fontSize: 13 }}> — 計算串關報酬率與期望值</span>
          </li>
        </ul>
      </div>

      <InlineCTA />

      {/* ===== 負責任博弈聲明 ===== */}
      <div style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 12, padding: '20px', marginTop: 32 }}>
        <p style={{ color: '#ef4444', fontWeight: 'bold', fontSize: 16, marginBottom: 12 }}>負責任博弈聲明</p>
        <p style={{ color: '#bbb', fontSize: 14, marginBottom: 8 }}>
          本文僅供參考，不構成投資或投注建議。體育投注的本質是娛樂，不是致富工具。
          即使是最好的分析策略，也無法保證每次投注都能獲利。請務必量力而行，只用閒錢投注。
        </p>
        <p style={{ color: '#bbb', fontSize: 14, margin: 0 }}>
          <strong>未滿 18 歲不得參與任何形式的博弈活動。</strong>
          如有問題性賭博傾向，請撥打衛生福利部安心專線 1925 或諮詢專線 1980。
          遇到疑似詐騙行為，請立即向<a href="https://165.npa.gov.tw" target="_blank" rel="noopener noreferrer" style={{ color: '#93c5fd' }}>165 反詐騙專線</a>通報。
          切勿追損，設定預算上限並嚴格遵守。
        </p>
      </div>
    </article>
  );
}
